import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from '../../Link/Link';

import '../form.less';

/**
 * ```import RenderField from '@zola/zola-ui/src/components/Form/RenderField';```
 *
 * Use with `redux-form`
 *  */
const RenderField = ({
  autoComplete, // not in InputFieldV2
  input, // not in InputFieldV2; in InputFieldV2Adapter
  label,
  labelClass, // not in InputFieldV2
  helperText,
  lastPassIgnore, // not in InputFieldV2
  type,
  id,
  required, // isRequired in InputFieldV2
  placeholder,
  disabled,
  onBlurFunction,
  className,
  meta, // not in InputFieldV2; in InputFieldV2Adapter
  autoFocus, // not in InputFieldV2
  onKeyDown, // not in InputFieldV2
  pattern, // not in InputFieldV2
  maxChars,
  addOn,
  onCopyLink, // not in InputFieldV2
  readOnly, // not in InputFieldV2
}) => {
  const hasError = meta && meta.touched && meta.error;
  const optionalAttr = {
    ...(autoComplete && { autoComplete }),
    ...(lastPassIgnore && { 'data-lpignore': true }),
  };
  return (
    <div
      className={classNames(
        'zola-ui render-field',
        { 'has-error': hasError },
        { 'has-add-on': addOn },
        className
      )}
    >
      {label && (
        <label htmlFor={id} className={labelClass}>
          {label}
          {required && <span className="text-danger danger-asterisk">*</span>}
        </label>
      )}
      <div className="input-field__container">
        {addOn && (
          <div className="input-addon">
            <div className="input-addon-icon">{addOn}</div>
          </div>
        )}
        <input
          {...input}
          {...optionalAttr}
          className="input-field"
          type={type}
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          onBlur={(e) => {
            if (onBlurFunction && input) {
              onBlurFunction(input.value);
            }
            if (input) {
              input.onBlur(e);
            }
          }}
          onKeyDown={onKeyDown}
          pattern={pattern}
          readOnly={readOnly}
        />
        {onCopyLink && (
          <Link onClick={onCopyLink} size="lg" className="copy-link-cta copy-link-cta__desktop">
            Copy Link
          </Link>
        )}
        {onCopyLink && (
          <Link onClick={onCopyLink} className="copy-link-cta copy-link-cta__mobile">
            <i onClick={onCopyLink} className="zola-ui-icon zola-ui-icon-link" />
          </Link>
        )}
        {maxChars && (
          <span
            className={classNames(
              'help-block max-characters-block',
              { 'text-danger': input.value.length > maxChars },
              { hidden: input.value.length <= maxChars }
            )}
          >
            You are {input.value.length - maxChars} characters over maximum.
          </span>
        )}
        {meta && meta.touched && meta.error && (
          <span className="help-block text-danger">{meta.error}</span>
        )}
        {meta && meta.asyncValidating && (
          <span className="help-block text-danger">Checking availability...</span>
        )}
      </div>
      {maxChars && (
        <div className="secondary-help-block max-characters-block">
          <span>Maximum {maxChars} characters</span>
          <span>
            {input.value.length}/{maxChars}
          </span>
        </div>
      )}
      {helperText && <div className="secondary-help-block max-characters-block">{helperText}</div>}
    </div>
  );
};

RenderField.propTypes = {
  autoComplete: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onBlur: PropTypes.func,
  }),
  id: PropTypes.string,
  label: PropTypes.node,
  helperText: PropTypes.string,
  labelClass: PropTypes.string,
  lastPassIgnore: PropTypes.bool,
  type: PropTypes.oneOf([
    'text',
    'email',
    'password',
    'tel',
    'number',
    'url',
    'search',
    'date',
    'time',
  ]).isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onBlurFunction: PropTypes.func,
  className: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    asyncValidating: PropTypes.bool,
  }),
  autoFocus: PropTypes.bool,
  onKeyDown: PropTypes.func,
  pattern: PropTypes.string,
  maxChars: PropTypes.number,
  addOn: PropTypes.string,
  onCopyLink: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default RenderField;
